import React from 'react';
import { machineDimensionText } from 'components/layout_and_quote_builder/utils/machineSheetText.js';

// A rectangle of a machine's sheet, sized on a scale SHARED across every bed on the page (maxMm is the largest
// machine dimension in the whole listing) so the beds stay comparable card-to-card — a bigger machine always
// draws bigger. The scale is COMPRESSED, not linear (see scaleFactor below): a small machine beside a much
// larger one used to collapse to an unreadable speck (a 510mm bed is only 16% of a 3200mm one), so a bed is
// sized by the SQUARE ROOT of its dimensions, which keeps the ordering and the aspect ratio while squeezing
// the range so small machines stay legible. Width is written along the bottom edge and height up the left edge
// (each number on the axis it measures). The bed self-identifies its machine type with centered icons AND
// color (so it doesn't rely on the column header alone): printing a printer (blue), die-cutting scissors
// (green), combined BOTH (purple). Used by the ManufacturingLine cards.
const STAGE_PX = 120;
export const KIND = {
  die_cutting: { color: '#2e7d32', background: 'rgba(46, 125, 50, 0.10)', icon: 'fa-scissors' },
  printing: { color: '#1565c0', background: 'rgba(21, 101, 192, 0.12)', icon: 'fa-print' },
  // ONE GLYPH PER PASS THE MACHINE RUNS, each in that pass's OWN icon and colour. A combined machine prints
  // AND die-cuts in a single pass, so it wears the blue printer and the green scissors side by side -- the
  // pair, in the colours the other beds wear them in, is what says "this one machine does both". Cogs said
  // only "a machine", which is the one thing every bed on the card already is. (The bed itself stays purple:
  // `color` is its border and its tint, and one machine is still one machine.) The same pair the
  // printing-and-die-cutting menu entry uses (quote_builder/SettingsMenu).
  combined: { color: '#6a1b9a', background: 'rgba(106, 27, 154, 0.12)', passes: ['printing', 'die_cutting'] }
};

// The glyphs one bed draws: its own icon, or -- for a machine that runs several passes -- one per pass, taken
// from that pass's own KIND entry so the two can never drift from the beds they refer to.
const glyphsFor = (style) => {
  if (style.passes) { return style.passes.map((pass) => ({ icon: KIND[pass].icon, color: KIND[pass].color })); }

  return style.icon ? [{ icon: style.icon, color: style.color }] : [];
};

// The faint machine icons (printer / scissors, or both) centered inside a bed, sized to the bed but kept
// legible (12-28px). Exported so the solution cards draw the same icons the ManufacturingLine cards do.
export const MachineIcon = ({ kind, widthPx, heightPx }) => {
  const style = KIND[kind];
  if (!style) { return null; }

  const glyphs = glyphsFor(style);
  if (glyphs.length === 0) { return null; }

  // Each glyph takes its share of the bed's WIDTH, so a pair sits side by side inside the same box a single
  // one had to itself. A one-glyph bed divides by one and is sized exactly as it always was.
  const size = Math.max(12, Math.min(28, Math.min(widthPx / glyphs.length, heightPx) * 0.5));
  return (
    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: `${Math.round(size * 0.25)}px` }}>
      {glyphs.map((glyph) => (
        <i key={glyph.icon} className={`fa ${glyph.icon}`} style={{ fontSize: `${size}px`, color: glyph.color, opacity: 0.6 }} aria-hidden="true" />
      ))}
    </span>
  );
};

const MachineSheetRectangle = ({ widthMm, heightMm, kind, maxMm }) => {
  const max = parseFloat(maxMm);
  if (!widthMm || !heightMm || !max || max <= 0) { return null; }

  // COMPRESSED (square-root) scale — see the header note. A bed's longest side maps to
  // STAGE_PX * sqrt(thisMax / max): the largest machine on the page still fills STAGE_PX, but everything below
  // it is pulled up toward legibility rather than shrinking linearly. The SAME factor scales both sides, so the
  // rectangle keeps its true proportions. Math.max keeps a floor so even the smallest bed stays clickable.
  const thisMax = Math.max(parseFloat(widthMm), parseFloat(heightMm));
  // A "0"/non-numeric dimension is truthy as a string, so it slips past the guard above; bail before it makes
  // sqrt(0) -> a zero divisor -> NaN pixel sizes. A bed with no real size has nothing to draw to scale.
  if (!(thisMax > 0)) { return null; }
  const scaleFactor = STAGE_PX / Math.sqrt(thisMax * max);
  const toPx = (valueMm) => Math.max(10, parseFloat(valueMm) * scaleFactor);
  const widthPx = toPx(widthMm);
  const heightPx = toPx(heightMm);
  const labelColor = '#777';
  const kindStyle = KIND[kind] || {};

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', marginTop: '24px', marginBottom: '20px' }}>
      {/* Height label next to the bed's left edge (the axis it measures). The generous top margin above drops
          the whole bed block clear of the machine-type text ("Offset"/"Pedal"), so the vertical number sits
          below that row instead of crowding it -- the compressed scale keeps beds large enough that the label
          reads beside the bed rather than needing to be moved away from it. */}
      <div style={{ fontSize: '11px', color: labelColor, lineHeight: 1, writingMode: 'vertical-rl', transform: 'rotate(180deg)', textAlign: 'center', height: `${heightPx}px`, marginRight: '3px' }}>{machineDimensionText(heightMm)}</div>
      <div style={{ position: 'relative', width: `${widthPx}px`, height: `${heightPx}px` }}>
        <div style={{
          width: '100%', height: '100%', boxSizing: 'border-box', borderRadius: '2px', overflow: 'hidden',
          border: `2px solid ${kindStyle.color}`, background: kindStyle.background,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <MachineIcon kind={kind} widthPx={widthPx} heightPx={heightPx} />
        </div>
        <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: '3px', fontSize: '11px', color: labelColor, lineHeight: 1, whiteSpace: 'nowrap' }}>{machineDimensionText(widthMm)}</div>
      </div>
    </div>
  );
};

export default MachineSheetRectangle;
